//frontend/src/pages/company/InventoryReceipt.jsx
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import CompanyLayout from '../../components/layout/CompanyLayout';
import { shopAPI, productAPI, inventoryAPI, warehouseAPI } from '../../services/api';
import deliveryService from '../../services/deliveryService';

const InventoryReceipt = () => {
  const { user, hasPermission } = useAuth();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('create'); // 'create' | 'history'
  
  // ============================================
  // NEW: Receipt Type State
  // ============================================
  const [receiptType, setReceiptType] = useState('purchase'); // 'purchase' | 'return'
  const [deliveries, setDeliveries] = useState([]); // Буцаалтын хүргэлтүүд
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  // ============================================
  
  // CREATE TAB - State
  const [shops, setShops] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [items, setItems] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  // HISTORY TAB - State
  const [receipts, setReceipts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [filters, setFilters] = useState({
    period: 'today',
    shop_id: '',
    warehouse_id: '',
    start_date: '',
    end_date: ''
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0
  });
  const [grandTotals, setGrandTotals] = useState({
    totalReceipts: 0,
    totalItems: 0,
    totalQuantity: 0,
    totalAmount: 0
  });
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // EDIT/DELETE State
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    fetchShops();
    fetchWarehouses();
    fetchDeliveries(); // ← НЭМЭГДСЭН
    if (activeTab === 'history') {
      fetchReceipts();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchReceipts();
    }
  }, [filters, pagination.currentPage]);

  const fetchShops = async () => {
    try {
      const response = await shopAPI.getAll();
      setShops(response.data.data || []);
    } catch (error) {
      console.error('Fetch shops error:', error);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const response = await warehouseAPI.getAll();
      setWarehouses(response.data.data || []);
    } catch (error) {
      console.error('Fetch warehouses error:', error);
    }
  };

  // ============================================
  // NEW: Fetch Deliveries
  // ============================================
  const fetchDeliveries = async () => {
    try {
      const response = await deliveryService.getDeliveries({
        status: 'in_transit,cancelled',
        limit: 1000
      });
      if (response.success) {
        // Буцаалт хийгдээгүй хүргэлтүүд
        const unreturnedDeliveries = response.data.filter(d => !d.return_receipt);
        setDeliveries(unreturnedDeliveries);
      }
    } catch (error) {
      console.error('Fetch deliveries error:', error);
    }
  };
  // ============================================

  const fetchProducts = async (shopId) => {
    try {
      if (!shopId) {
        setProducts([]);
        return;
      }
      const response = await productAPI.getAll({ shop_id: shopId, limit: 1000 });
      setProducts(response.data.data || []);
    } catch (error) {
      console.error('Fetch products error:', error);
    }
  };

  const getDateRange = (period) => {
    const today = new Date();
    let startDate, endDate;

    switch (period) {
      case 'today':
        startDate = endDate = today.toISOString().split('T')[0];
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = endDate = yesterday.toISOString().split('T')[0];
        break;
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        startDate = weekAgo.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'month':
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        startDate = monthAgo.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'custom':
        startDate = filters.start_date;
        endDate = filters.end_date;
        break;
      default:
        startDate = endDate = '';
    }

    return { startDate, endDate };
  };

  const fetchReceipts = async () => {
    try {
      setLoadingHistory(true);
      const { startDate, endDate } = getDateRange(filters.period);
      
      const params = {
        page: pagination.currentPage,
        limit: 20,
        ...(filters.shop_id && { shop_id: filters.shop_id }),
        ...(filters.warehouse_id && { warehouse_id: filters.warehouse_id }),
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate })
      };

      const response = await inventoryAPI.getAllReceipts(params);
      setReceipts(response.data.data || []);
      setPagination({
        currentPage: response.data.pagination.currentPage,
        totalPages: response.data.pagination.totalPages,
        totalItems: response.data.pagination.totalItems
      });
      setGrandTotals(response.data.summary || {
        totalReceipts: 0,
        totalItems: 0,
        totalQuantity: 0,
        totalAmount: 0
      });
    } catch (error) {
      console.error('Fetch receipts error:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchReceiptDetail = async (id) => {
    try {
      const response = await inventoryAPI.getReceiptById(id);
      setSelectedReceipt(response.data.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Fetch receipt detail error:', error);
      alert('Дэлгэрэнгүй мэдээлэл татахад алдаа гарлаа');
    }
  };

  const handleShopChange = (shopId) => {
    const shop = shops.find(s => s.id === parseInt(shopId));
    setSelectedShop(shop);
    setItems([]);
    fetchProducts(shopId);
  };

  // ============================================
  // NEW: Handle Delivery Change
  // ============================================
  const handleDeliveryChange = async (deliveryId) => {
    try {
      const delivery = deliveries.find(d => d.id === parseInt(deliveryId));
      if (!delivery) return;

      setSelectedDelivery(delivery);
      
      // Shop-г автоматаар сонгох
      const shop = shops.find(s => s.id === delivery.shop_id);
      setSelectedShop(shop);
      
      // Outbound items-г татаж авах
      const response = await deliveryService.getDeliveryById(delivery.id);
      
      if (response.success && response.data.outbound?.items) {
        // Барааг автоматаар fill хийх
        const returnItems = response.data.outbound.items.map(item => ({
          product_id: item.product_id,
          product_name: item.product?.product_name || 'Unknown',
          sku: item.product?.sku || '',
          quantity: item.quantity,
          unit_price: parseFloat(item.unit_price),
          base_price: parseFloat(item.unit_price)
        }));
        setItems(returnItems);
      }
    } catch (error) {
      console.error('Fetch delivery detail error:', error);
      alert('Хүргэлтийн мэдээлэл татахад алдаа гарлаа');
    }
  };
  // ============================================

  const handleAddProduct = (product) => {
    const exists = items.find(item => item.product_id === product.id);
    if (exists) {
      alert('Энэ бараа аль хэдийн нэмэгдсэн байна');
      return;
    }

    setItems([...items, {
      product_id: product.id,
      product_name: product.product_name,
      sku: product.sku,
      base_price: product.base_price || 0,
      quantity: 1,
      unit_price: product.base_price || 0
    }]);
    setShowProductModal(false);
    setSearchTerm('');
  };

  const handleUpdateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      // Validation
      if (receiptType === 'return' && !selectedDelivery) {
        alert('Хүргэлт сонгоно уу');
        return;
      }

      if (receiptType === 'purchase' && !selectedShop) {
        alert('Онлайн шоп сонгоно уу');
        return;
      }

      if (receiptType === 'purchase' && !selectedShop.warehouse_id) {
        alert('Энэ шопод агуулах холбогдоогүй байна');
        return;
      }

      if (items.length === 0) {
        alert('Бараа нэмнэ үү');
        return;
      }

      for (const item of items) {
        if (!item.quantity || parseInt(item.quantity) <= 0) {
          alert(`"${item.product_name}" барааны тоо ширхэг 0-с их байх ёстой`);
          return;
        }
        if (!item.unit_price || parseFloat(item.unit_price) <= 0) {
          alert(`"${item.product_name}" барааны үнэ 0-с их байх ёстой`);
          return;
        }
      }

      setLoading(true);

      const receiptData = {
        shop_id: selectedShop?.id,
        warehouse_id: selectedShop?.warehouse_id,
        notes: notes,
        items: items.map(item => ({
          product_id: item.product_id,
          quantity: parseInt(item.quantity),
          unit_price: parseFloat(item.unit_price)
        }))
      };

      // ============================================
      // NEW: Add delivery_id for return
      // ============================================
      if (receiptType === 'return' && selectedDelivery) {
        receiptData.delivery_id = selectedDelivery.id;
      }

      // ============================================
      // NEW: Use different API endpoint
      // ============================================
      if (receiptType === 'return') {
        await inventoryAPI.createReturnReceipt(receiptData);
        alert('Буцаалт амжилттай бүртгэгдлээ!');
      } else {
        await inventoryAPI.createReceipt(receiptData);
        alert('Орлого амжилттай бүртгэгдлээ!');
      }
      // ============================================
      
      // Reset form
      setSelectedShop(null);
      setSelectedDelivery(null);
      setItems([]);
      setNotes('');
      setReceiptType('purchase');
      
      setActiveTab('history');
      
    } catch (error) {
      console.error('Create receipt error:', error);
      alert(error.response?.data?.message || 'Орлого бүртгэхэд алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReceipt = async (id) => {
    if (!window.confirm('Орлого устгах уу? Энэ үйлдлийг буцаах боломжгүй!')) {
      return;
    }

    try {
      await inventoryAPI.deleteReceipt(id);
      alert('Орлого устгагдлаа');
      setShowDetailModal(false);
      setSelectedReceipt(null);
      fetchReceipts();
    } catch (error) {
      console.error('Delete receipt error:', error);
      alert(error.response?.data?.message || 'Устгахад алдаа гарлаа');
    }
  };

  const handleEditReceipt = (receipt) => {
    setEditingReceipt(receipt);
    setShowEditModal(true);
  };

  const handleUpdateReceipt = async () => {
    try {
      if (!editingReceipt) return;

      await inventoryAPI.updateReceipt(editingReceipt.id, {
        notes: editingReceipt.notes
      });

      alert('Орлого шинэчлэгдлээ');
      setShowEditModal(false);
      setEditingReceipt(null);
      fetchReceiptDetail(editingReceipt.id);
      fetchReceipts();
    } catch (error) {
      console.error('Update receipt error:', error);
      alert(error.response?.data?.message || 'Засахад алдаа гарлаа');
    }
  };

  const getTotalAmount = () => {
    return items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const getTotalQuantity = () => {
    return items.reduce((sum, item) => sum + parseInt(item.quantity || 0), 0);
  };

  const filteredProducts = products.filter(p => 
    p.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.sku?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPeriodLabel = (period) => {
    const labels = {
      today: 'Өнөөдөр',
      yesterday: 'Өчигдөр',
      week: '7 хоног',
      month: 'Энэ сар',
      custom: 'Өөрөө сонгох'
    };
    return labels[period] || 'Өнөөдөр';
  };

  // ============================================
  // NEW: Get Receipt Type Label
  // ============================================
  const getReceiptTypeLabel = (type) => {
    const labels = {
      purchase: '💰 Бараа орлого',
      return: '🔄 Буцаалт',
      transfer: '📦 Шилжүүлэг',
      adjustment: '⚙️ Тохируулга'
    };
    return labels[type] || type;
  };

  return (
    <CompanyLayout>
      <div className="space-y-4">
        {/* HEADER */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center">
              <span className="text-3xl">📥</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Орлого удирдах</h1>
              <p className="text-sm text-gray-600 mt-1">Агуулахад бараа орлогодуулах болон түүх харах</p>
            </div>
          </div>
        </div>

        {/* TABS */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="flex">
              {hasPermission('inventory.receive') && (
              <button
                onClick={() => setActiveTab('create')}
                className={`flex-1 px-6 py-4 text-sm font-semibold transition ${
                  activeTab === 'create'
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                📥 Орлого бүртгэх
              </button>
              )}
              <button
                onClick={() => setActiveTab('history')}
                className={`flex-1 px-6 py-4 text-sm font-semibold transition ${
                  activeTab === 'history'
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                📋 Орлогын түүх
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* CREATE TAB */}
            {activeTab === 'create' && (
              <div className="space-y-6">
                {/* ============================================ */}
                {/* NEW: RECEIPT TYPE SELECTION */}
                {/* ============================================ */}
                <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Орлогын төрөл *
                  </label>
                  <div className={`grid gap-3 ${hasPermission('inventory.return') ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setReceiptType('purchase');
                        setSelectedDelivery(null);
                        setItems([]);
                      }}
                      className={`p-4 border-2 rounded-lg transition ${
                        receiptType === 'purchase'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-2xl mb-2">💰</div>
                      <div className="font-semibold text-gray-900">Барааны орлого бүртгэх</div>
                      <div className="text-xs text-gray-500 mt-1">Агуулахад бараа орлогодуулах</div>
                    </button>
                    
                    {hasPermission('inventory.return') && (
                      <button
                        type="button"
                        onClick={() => {
                          setReceiptType('return');
                          setSelectedShop(null);
                          setItems([]);
                        }}
                        className={`p-4 border-2 rounded-lg transition ${
                          receiptType === 'return'
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-2xl mb-2">🔄</div>
                        <div className="font-semibold text-gray-900">Буцаалт</div>
                        <div className="text-xs text-gray-500 mt-1">Хүргэлтээс буцаасан бараа</div>
                      </button>
                      )}
                  </div>
                </div>
                {/* ============================================ */}

                {/* ============================================ */}
                {/* NEW: DELIVERY SELECTION (Return only) */}
                {/* ============================================ */}
                {receiptType === 'return' && (
                  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Хүргэлтийн дугаар *
                    </label>
                    <select
                      value={selectedDelivery?.id || ''}
                      onChange={(e) => handleDeliveryChange(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition"
                    >
                      <option value="">-- Хүргэлт сонгох --</option>
                      {deliveries.map((delivery) => (
                        <option key={delivery.id} value={delivery.id}>
                          {delivery.delivery_number} - {delivery.customer_name} ({delivery.status === 'cancelled' ? 'Цуцалсан' : 'Хүргэж байна'})
                        </option>
                      ))}
                    </select>
                    
                    {selectedDelivery && (
                      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="text-sm">
                          <div className="font-semibold text-gray-900 mb-1">
                            {selectedDelivery.customer_name}
                          </div>
                          <div className="text-gray-600">
                            📍 {selectedDelivery.address}
                          </div>
                          <div className="text-gray-600 mt-1">
                            📞 {selectedDelivery.customer_phone}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* ============================================ */}

                {/* SHOP SELECTION (Purchase only) */}
                {receiptType === 'purchase' && (
                  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Онлайн шоп *
                    </label>
                    <select
                      value={selectedShop?.id || ''}
                      onChange={(e) => handleShopChange(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    >
                      <option value="">-- Шоп сонгох --</option>
                      {shops.map((shop) => (
                        <option key={shop.id} value={shop.id}>
                          {shop.shop_name}
                        </option>
                      ))}
                    </select>
                    
                    {selectedShop && selectedShop.warehouse && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">🏭</span>
                          <div className="text-sm">
                            <span className="text-gray-600">Агуулах:</span>{' '}
                            <span className="font-semibold text-gray-900">{selectedShop.warehouse.warehouse_name}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ITEMS LIST */}
                {((receiptType === 'purchase' && selectedShop) || (receiptType === 'return' && selectedDelivery)) && (
                  <>
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                      <div className="p-5 border-b border-gray-200 bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <h2 className="text-lg font-semibold text-gray-900">Барааны жагсаалт</h2>
                            <span className="px-2.5 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                              {items.length}
                            </span>
                          </div>
                          {receiptType === 'purchase' && (
                            <button
                              onClick={() => setShowProductModal(true)}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition flex items-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                              <span>Бараа нэмэх</span>
                            </button>
                          )}
                        </div>
                      </div>

                      {items.length === 0 ? (
                        <div className="text-center py-12 px-6">
                          <div className="text-5xl mb-3">📦</div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">Бараа нэмнэ үү</h3>
                          <p className="text-sm text-gray-600 mb-4">Эхний барааг нэмээд эхлээрэй</p>
                          {receiptType === 'purchase' && (
                            <button
                              onClick={() => setShowProductModal(true)}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition"
                            >
                              <span>+</span>
                              <span>Бараа нэмэх</span>
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {items.map((item, index) => (
                            <div key={index} className="p-4 hover:bg-gray-50 transition group">
                              <div className="grid grid-cols-12 gap-3 items-center">
                                <div className="col-span-4">
                                  <div className="flex items-start gap-2">
                                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                      <span className="text-lg">📦</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-gray-900 truncate text-sm">{item.product_name}</div>
                                      <div className="text-xs text-gray-500">SKU: {item.sku}</div>
                                    </div>
                                  </div>
                                </div>

                                <div className="col-span-2">
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Тоо ширхэг</label>
                                  <input
                                    type="number"
                                    min="1"
                                    value={item.quantity}
                                    onChange={(e) => handleUpdateItem(index, 'quantity', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-sm text-center"
                                    disabled={receiptType === 'return'}
                                  />
                                </div>

                                <div className="col-span-3">
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Нэгж үнэ</label>
                                  <div className="relative">
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={item.unit_price}
                                      onChange={(e) => handleUpdateItem(index, 'unit_price', e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-sm"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₮</span>
                                  </div>
                                </div>

                                <div className="col-span-2">
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Дүн</label>
                                  <div className="text-sm font-semibold text-blue-600">
                                    {(item.quantity * item.unit_price).toLocaleString()}₮
                                  </div>
                                </div>

                                <div className="col-span-1 flex justify-end">
                                  {receiptType === 'purchase' && (
                                    <button
                                      onClick={() => handleRemoveItem(index)}
                                      className="w-8 h-8 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition opacity-0 group-hover:opacity-100 flex items-center justify-center"
                                      title="Устгах"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {items.length > 0 && (
                        <div className={`border-t p-4 ${receiptType === 'return' ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                              <div className="text-xs font-medium text-gray-600 mb-1">Нийт бараа</div>
                              <div className={`text-2xl font-bold ${receiptType === 'return' ? 'text-green-600' : 'text-blue-600'}`}>{items.length}</div>
                            </div>
                            <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                              <div className="text-xs font-medium text-gray-600 mb-1">Нийт ширхэг</div>
                              <div className={`text-2xl font-bold ${receiptType === 'return' ? 'text-green-600' : 'text-blue-600'}`}>{getTotalQuantity()}</div>
                            </div>
                            <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                              <div className="text-xs font-medium text-gray-600 mb-1">Нийт дүн</div>
                              <div className={`text-2xl font-bold ${receiptType === 'return' ? 'text-green-600' : 'text-blue-600'}`}>{getTotalAmount().toLocaleString()}₮</div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* NOTES */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Тэмдэглэл
                      </label>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition resize-none text-sm"
                        rows="3"
                        placeholder={receiptType === 'return' ? 'Буцаалтын тэмдэглэл...' : 'Орлогын тэмдэглэл...'}
                      ></textarea>
                    </div>

                    {/* ACTIONS */}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          setSelectedShop(null);
                          setSelectedDelivery(null);
                          setItems([]);
                          setNotes('');
                          setReceiptType('purchase');
                        }}
                        className="flex-1 px-5 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition"
                      >
                        Болих
                      </button>
                      <button
                        onClick={handleSubmit}
                        disabled={loading || items.length === 0}
                        className={`flex-1 px-5 py-3 text-white rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                          receiptType === 'return' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                      >
                        {loading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Хадгалж байна...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>{receiptType === 'return' ? 'Буцаалт бүртгэх' : 'Орлого бүртгэх'}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* HISTORY TAB */}
            {activeTab === 'history' && (
              <div className="space-y-6">
                {/* FILTERS */}
                <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">🔍 Шүүлтүүр</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">Хугацаа</label>
                      <select
                        value={filters.period}
                        onChange={(e) => setFilters({ ...filters, period: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-sm"
                      >
                        <option value="today">Өнөөдөр</option>
                        <option value="yesterday">Өчигдөр</option>
                        <option value="week">7 хоног</option>
                        <option value="month">Энэ сар</option>
                        <option value="custom">Өөрөө сонгох</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">Онлайн шоп</label>
                      <select
                        value={filters.shop_id}
                        onChange={(e) => setFilters({ ...filters, shop_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-sm"
                      >
                        <option value="">Бүгд</option>
                        {shops.map((shop) => (
                          <option key={shop.id} value={shop.id}>{shop.shop_name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">Агуулах</label>
                      <select
                        value={filters.warehouse_id}
                        onChange={(e) => setFilters({ ...filters, warehouse_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-sm"
                      >
                        <option value="">Бүгд</option>
                        {warehouses.map((warehouse) => (
                          <option key={warehouse.id} value={warehouse.id}>{warehouse.warehouse_name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">Хайлт</label>
                      <input
                        type="text"
                        placeholder="Receipt дугаар..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-sm"
                      />
                    </div>
                  </div>

                  {filters.period === 'custom' && (
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-2">Эхлэх огноо</label>
                        <input
                          type="date"
                          value={filters.start_date}
                          onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-2">Дуусах огноо</label>
                        <input
                          type="date"
                          value={filters.end_date}
                          onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* RECEIPTS LIST */}
                {loadingHistory ? (
                  <div className="text-center py-12">
                    <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Уншиж байна...</p>
                  </div>
                ) : receipts.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">📋</div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Орлого олдсонгүй</h3>
                    <p className="text-gray-600">Сонгосон хугацаанд орлого бүртгэгдээгүй байна</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-3">
                      {receipts.map((receipt) => (
                        <div key={receipt.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-3">
                                <h4 className="text-lg font-bold text-gray-900">{receipt.receipt_number}</h4>
                                <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                  receipt.source_type === 'return' 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-blue-100 text-blue-700'
                                }`}>
                                  {getReceiptTypeLabel(receipt.source_type)}
                                </span>
                                <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-semibold">
                                  {receipt.status}
                                </span>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-gray-600">📅 Огноо:</span>{' '}
                                  <span className="font-medium text-gray-900">
                                    {new Date(receipt.receipt_date).toLocaleDateString('mn-MN')} {new Date(receipt.created_at).toLocaleTimeString('mn-MN', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-gray-600">🛍️ Шоп:</span>{' '}
                                  <span className="font-medium text-gray-900">{receipt.shop?.shop_name}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600">🏭 Агуулах:</span>{' '}
                                  <span className="font-medium text-gray-900">{receipt.warehouse?.warehouse_name}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600">📦 Бараа:</span>{' '}
                                  <span className="font-medium text-gray-900">
                                    {receipt.summary?.totalItems} төрөл ({receipt.summary?.totalQuantity} ш)
                                  </span>
                                </div>
                                {receipt.delivery && (
                                  <div className="col-span-2">
                                    <span className="text-gray-600">🚚 Хүргэлт:</span>{' '}
                                    <span className="font-medium text-gray-900">
                                      {receipt.delivery.delivery_number} - {receipt.delivery.customer_name}
                                    </span>
                                  </div>
                                )}
                                <div className="col-span-2">
                                  <span className="text-gray-600">👤 Бүртгэсэн:</span>{' '}
                                  <span className="font-medium text-gray-900">
                                    {receipt.creator?.full_name || receipt.creator?.username || '-'}
                                  </span>
                                </div>
                              </div>

                              <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
                                <div className={`text-2xl font-bold ${receipt.source_type === 'return' ? 'text-green-600' : 'text-blue-600'}`}>
                                  {receipt.summary?.totalAmount?.toLocaleString()}₮
                                </div>
                                <button
                                  onClick={() => fetchReceiptDetail(receipt.id)}
                                  className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg font-medium hover:bg-blue-100 transition flex items-center gap-2 text-sm"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                  <span>Дэлгэрэнгүй</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* SUMMARY */}
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5">
                      <h3 className="text-sm font-semibold text-blue-900 mb-3">
                        📊 НИЙТ ДҮНГИЙН МЭДЭЭЛЭЛ ({getPeriodLabel(filters.period)})
                      </h3>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-blue-600 font-medium">Нийт орлого:</div>
                          <div className="text-2xl font-bold text-blue-900">{grandTotals.totalReceipts}</div>
                        </div>
                        <div>
                          <div className="text-blue-600 font-medium">Нийт бараа:</div>
                          <div className="text-2xl font-bold text-blue-900">{grandTotals.totalItems} төрөл</div>
                        </div>
                        <div>
                          <div className="text-blue-600 font-medium">Нийт ширхэг:</div>
                          <div className="text-2xl font-bold text-blue-900">{grandTotals.totalQuantity} ш</div>
                        </div>
                        <div>
                          <div className="text-blue-600 font-medium">Нийт дүн:</div>
                          <div className="text-2xl font-bold text-blue-900">{grandTotals.totalAmount.toLocaleString()}₮</div>
                        </div>
                      </div>
                    </div>

                    {/* PAGINATION */}
                    {pagination.totalPages > 1 && (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => setPagination({ ...pagination, currentPage: pagination.currentPage - 1 })}
                          disabled={pagination.currentPage === 1}
                          className="px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ◀ Өмнөх
                        </button>
                        
                        <div className="flex items-center gap-2">
                          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                            <button
                              key={page}
                              onClick={() => setPagination({ ...pagination, currentPage: page })}
                              className={`w-10 h-10 rounded-lg font-medium transition ${
                                page === pagination.currentPage
                                  ? 'bg-blue-600 text-white'
                                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          ))}
                        </div>

                        <button
                          onClick={() => setPagination({ ...pagination, currentPage: pagination.currentPage + 1 })}
                          disabled={pagination.currentPage === pagination.totalPages}
                          className="px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Дараах ▶
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PRODUCT SELECTION MODAL */}
      {showProductModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] flex flex-col shadow-xl">
            <div className="p-5 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  Бараа сонгох
                </h3>
                <button
                  onClick={() => {
                    setShowProductModal(false);
                    setSearchTerm('');
                  }}
                  className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg transition flex items-center justify-center"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Бараа хайх (нэр, SKU)..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-sm"
                  autoFocus
                />
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              {filteredProducts.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-5xl mb-3">🔍</div>
                  <p className="text-sm text-gray-600">Бараа олдсонгүй</p>
                </div>
              ) : (
                <div className="grid gap-2">
                  {filteredProducts.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => handleAddProduct(product)}
                      className="w-full p-3 bg-white border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition text-left group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center group-hover:scale-105 transition">
                          <span className="text-lg">📦</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate text-sm">{product.product_name}</div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-gray-500">SKU: {product.sku}</span>
                            {product.base_price && (
                              <span className="text-xs font-semibold text-blue-600">
                                {product.base_price.toLocaleString()}₮
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          <div className="w-7 h-7 bg-blue-600 text-white rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DETAIL MODAL */}
      {showDetailModal && selectedReceipt && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] flex flex-col shadow-xl">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {selectedReceipt.source_type === 'return' ? '🔄 Буцаалтын дэлгэрэнгүй' : '📥 Орлогын дэлгэрэнгүй'}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">{selectedReceipt.receipt_number}</p>
                </div>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedReceipt(null);
                  }}
                  className="w-10 h-10 bg-white hover:bg-gray-100 rounded-lg transition flex items-center justify-center shadow"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3">ҮНДСЭН МЭДЭЭЛЭЛ</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Баримтын дугаар:</span>
                    <div className="font-semibold text-gray-900 mt-1">{selectedReceipt.receipt_number}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Төрөл:</span>
                    <div className="font-semibold text-gray-900 mt-1">{getReceiptTypeLabel(selectedReceipt.source_type)}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Огноо:</span>
                    <div className="font-semibold text-gray-900 mt-1">
                      {new Date(selectedReceipt.receipt_date).toLocaleDateString('mn-MN')} {new Date(selectedReceipt.created_at).toLocaleTimeString('mn-MN')}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">Онлайн шоп:</span>
                    <div className="font-semibold text-gray-900 mt-1">{selectedReceipt.shop?.shop_name}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Агуулах:</span>
                    <div className="font-semibold text-gray-900 mt-1">{selectedReceipt.warehouse?.warehouse_name}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Бүртгэсэн:</span>
                    <div className="font-semibold text-gray-900 mt-1">
                      {selectedReceipt.creator?.full_name || selectedReceipt.creator?.username || '-'}
                    </div>
                  </div>
                  {selectedReceipt.delivery && (
                    <div className="col-span-2">
                      <span className="text-gray-600">Хүргэлт:</span>
                      <div className="font-semibold text-gray-900 mt-1">
                        {selectedReceipt.delivery.delivery_number} - {selectedReceipt.delivery.customer_name}
                      </div>
                    </div>
                  )}
                  <div className="col-span-2">
                    <span className="text-gray-600">Тэмдэглэл:</span>
                    <div className="font-medium text-gray-900 mt-1">{selectedReceipt.notes || '-'}</div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200">
                <div className="p-4 bg-gray-50 border-b border-gray-200">
                  <h4 className="font-semibold text-gray-900">БАРААНЫ ЖАГСААЛТ</h4>
                </div>
                <div className="divide-y divide-gray-100">
                  {selectedReceipt.items?.map((item, index) => (
                    <div key={item.id} className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">
                            {index + 1}. {item.product?.product_name}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            SKU: {item.product?.sku}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">
                            {item.quantity} ш × {parseFloat(item.unit_price).toLocaleString()}₮
                          </div>
                          <div className={`text-lg font-bold mt-1 ${selectedReceipt.source_type === 'return' ? 'text-green-600' : 'text-blue-600'}`}>
                            = {(item.quantity * parseFloat(item.unit_price)).toLocaleString()}₮
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className={`p-4 border-t-2 ${selectedReceipt.source_type === 'return' ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
                  <div className="flex items-center justify-between text-lg font-bold">
                    <span className="text-gray-900">
                      НИЙТ: {selectedReceipt.items?.length} төрөл × {selectedReceipt.items?.reduce((sum, item) => sum + item.quantity, 0)} ширхэг
                    </span>
                    <span className={`text-2xl ${selectedReceipt.source_type === 'return' ? 'text-green-600' : 'text-blue-600'}`}>
                      {selectedReceipt.items?.reduce((sum, item) => sum + (item.quantity * parseFloat(item.unit_price)), 0).toLocaleString()}₮
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex gap-3">
                {hasPermission('inventory.receive') && (
                  <>
                    <button
                      onClick={() => handleEditReceipt(selectedReceipt)}
                      className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span>Засах</span>
                    </button>
                    <button
                      onClick={() => handleDeleteReceipt(selectedReceipt.id)}
                      className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span>Устгах</span>
                    </button>
                  </>
                )}
                <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedReceipt(null);
                }}
                className={`${hasPermission('inventory.receive') ? 'flex-1' : 'w-full'} px-6 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition`}
              >
                Хаах
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {showEditModal && editingReceipt && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-xl">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Орлого засах</h3>
              <p className="text-sm text-gray-600 mt-1">{editingReceipt.receipt_number}</p>
            </div>

            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Тэмдэглэл
              </label>
              <textarea
                value={editingReceipt.notes || ''}
                onChange={(e) => setEditingReceipt({ ...editingReceipt, notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition resize-none"
                rows="4"
                placeholder="Тэмдэглэл..."
              ></textarea>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 flex gap-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingReceipt(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                Болих
              </button>
              <button
                onClick={handleUpdateReceipt}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Хадгалах
              </button>
            </div>
          </div>
        </div>
      )}
    </CompanyLayout>
  );
};

export default InventoryReceipt;